<template>
    <AdminPanelTemplate>
        <template #breadcrumbs>
            <Breadcrumbs :items="[{ title: 'Payout Events', to: useRoute().build(PayoutEvent.routes().index) }, { title: data.name }]" />
        </template>
        <template #content>
            <v-container fluid>
                <!-- Header -->
                <div class="d-flex align-center justify-space-between mb-4">
                    <div>
                        <h2 class="text-h5 font-weight-bold">{{ data.name }}</h2>
                        <p class="text-body-2 text-medium-emphasis">Event date: {{ data.date ? data.date : 'to be confirmed' }}</p>
                    </div>
                    <div class="d-flex ga-2">
                        <GenericActionDialog confirm-btn-text="Save" title="Edit Payout Event" :disabled="false" @confirm="confirm($event)">
                            <template #activator="{ props: activatorProps }">
                                <v-btn v-bind="activatorProps" color="primary" variant="outlined" prepend-icon="mdi-pencil" :loading="processing">Edit event</v-btn>
                            </template>
                            <template #body>
                                <UpdateBundleDialog :item="data" />
                            </template>
                        </GenericActionDialog>
                        <GenericActionDialog title="Confirm Payout Event" confirm-btn-text="Confirm" confirm-btn-color="success" :loading="confirming" @confirm="confirmEvent" @cancel="confirmError = ''">
                            <template #activator="{ props: activatorProps }">
                                <v-tooltip :text="confirmDisabledReason ?? ''" :disabled="canConfirm">
                                    <template #activator="{ props: tooltipProps }">
                                        <span v-bind="tooltipProps">
                                            <v-btn v-bind="canConfirm ? activatorProps : {}" :disabled="!canConfirm" color="success" variant="flat"> Confirm event </v-btn>
                                        </span>
                                    </template>
                                </v-tooltip>
                            </template>
                            <template #body>
                                <p class="mb-2">By clicking <strong>CONFIRM</strong>:</p>
                                <ul class="ml-4 mb-4">
                                    <li>Payout Price/tCO2e, Event Date and Owners list will no longer be editable</li>
                                    <li>Payouts will be created and calculated automatically based on the Payout Price/tCO2e for the event</li>
                                </ul>
                                <p class="font-weight-bold text-error">This action cannot be undone. Do you confirm?</p>
                                <v-alert v-if="confirmError" type="error" density="compact" class="mt-4">{{ confirmError }}</v-alert>
                            </template>
                        </GenericActionDialog>
                    </div>
                </div>

                <!-- Summary Cards -->
                <v-row class="mb-4">
                    <v-col cols="12" sm="6" md="3">
                        <v-card>
                            <v-card-text class="text-center">
                                <p class="text-body-2 text-medium-emphasis">Payout Price/tCO2e (MXN)</p>
                                <p class="text-h6">{{ formatNumber(data.payout_price, 2) || '-' }}</p>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card>
                            <v-card-text class="text-center">
                                <p class="text-body-2 text-medium-emphasis">Owners</p>
                                <p class="text-h6">{{ data.land_owners_count ?? 0 }}</p>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card>
                            <v-card-text class="text-center">
                                <p class="text-body-2 text-medium-emphasis">
                                    Total Payouts (MXN)
                                    <v-tooltip v-if="!isConfirmed" location="top" max-width="260">
                                        <template #activator="{ props: tooltipProps }">
                                            <v-icon
                                                v-bind="tooltipProps"
                                                icon="mdi-information-outline"
                                                size="x-small"
                                                class="ms-1"
                                                role="button"
                                                tabindex="0"
                                                aria-label="How the projected total is calculated"
                                                data-testid="projected-total-info"
                                            />
                                        </template>
                                        Projected total based on the current owners and payout price. Final amounts are set when the event is confirmed.
                                    </v-tooltip>
                                </p>
                                <p class="text-h6">{{ formatNumber(data.total_payout_amount ?? 0, 2) }}</p>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card>
                            <v-card-text class="text-center">
                                <p class="text-body-2 text-medium-emphasis">Event Status</p>
                                <v-chip :color="PayoutEvent.getStatusColor(data.status)" size="small">
                                    {{ data.status }}
                                </v-chip>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Tabs -->
                <v-card>
                    <v-tabs v-model="tab" color="primary">
                        <v-tab value="owners">Owners</v-tab>
                        <v-tab value="payouts">Payouts</v-tab>
                    </v-tabs>
                    <v-card-text>
                        <v-tabs-window v-model="tab">
                            <!-- Payouts Tab -->
                            <v-tabs-window-item value="payouts">
                                <div v-if="selectedPayoutIds.length" class="d-flex align-center ga-2 mb-2 pa-2 rounded" style="background: rgba(223, 255, 245, 0.26); border: 1px solid rgba(32, 155, 115, 0.26)">
                                    <span class="text-body-2">{{ selectedPayoutIds.length }} selected</span>
                                    <v-btn color="success" size="small" variant="flat" :loading="bulkUpdating" @click="markSelectedAsPaid">Mark as Paid</v-btn>
                                    <v-btn size="small" variant="text" @click="selectedPayoutIds = []">Clear</v-btn>
                                </div>
                                <v-table v-if="payouts.length" density="compact">
                                    <thead>
                                        <tr>
                                            <th style="width: 40px">
                                                <v-checkbox-btn :model-value="allPayoutsSelected" :indeterminate="somePayoutsSelected && !allPayoutsSelected" @update:model-value="toggleAllPayouts" />
                                            </th>
                                            <th class="text-center" style="width: 60px">Actions</th>
                                            <th>Owner</th>
                                            <th>Block</th>
                                            <th class="text-center">Carbon (tCO2e)</th>
                                            <th class="text-center">Price</th>
                                            <th class="text-center">Amount</th>
                                            <th>Currency</th>
                                            <th>Status</th>
                                            <th>Transaction</th>
                                            <th>Note</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="payout in payouts" :key="payout.id">
                                            <td>
                                                <v-checkbox-btn :model-value="selectedPayoutIds.includes(payout.id)" :disabled="!isPayoutSelectable(payout)" @update:model-value="togglePayout(payout.id)" />
                                            </td>
                                            <td class="text-center">
                                                <GenericActionDialog confirm-btn-text="Save" title="Edit Payout" :disabled="false" @confirm="confirm($event)">
                                                    <template #activator="{ props: activatorProps }">
                                                        <v-btn v-bind="activatorProps" icon="mdi-pencil" size="x-small" variant="text" color="primary" />
                                                    </template>
                                                    <template #body>
                                                        <UpdatePayoutDialog :item="payout" />
                                                    </template>
                                                </GenericActionDialog>
                                            </td>
                                            <td>
                                                <Link v-if="payout.owner?.data" :href="useOwnerRepository().query.buildGetUrl(payout.owner.data.id).endpoint">{{ payout.owner.data.name }}</Link>
                                                <span v-else>-</span>
                                            </td>
                                            <td>
                                                <Link v-if="payout.block?.data" :href="useBlockRepository().query.show(payout.block.data.id).endpoint">{{ payout.block.data.block_serial_code }}</Link>
                                            </td>
                                            <td class="text-center">{{ payout.block?.data?.carbon_block_tco2e }}</td>
                                            <td class="text-center">{{ payout.price }}</td>
                                            <td class="text-center">{{ payout.amount }}</td>
                                            <td>{{ payout.currency }}</td>
                                            <td>
                                                <v-chip :color="Payout.getStatusColor(payout.status)" size="x-small">{{ payout.status }}</v-chip>
                                            </td>
                                            <td>
                                                <Link v-if="payout.transaction?.data" :href="useTransactionRepository().query.show(payout.transaction.data.id).endpoint">{{ payout.transaction.data.transaction_code }}</Link>
                                            </td>
                                            <td>{{ payout.note }}</td>
                                        </tr>
                                    </tbody>
                                </v-table>
                                <p v-else class="text-body-2 text-medium-emphasis pa-4">No payouts yet. Payouts are created when the event is confirmed.</p>
                            </v-tabs-window-item>

                            <!-- Owners Tab -->
                            <v-tabs-window-item value="owners">
                                <div class="d-flex justify-space-between align-center mb-2">
                                    <div v-if="selectedOwnerIds.length" class="d-flex align-center ga-2 pa-2 rounded" style="background: rgba(223, 255, 245, 0.26); border: 1px solid rgba(32, 155, 115, 0.26)">
                                        <span class="text-body-2">{{ selectedOwnerIds.length }} owner(s) selected ({{ selectedOwnerPayoutCount }} unpaid payouts)</span>
                                        <v-btn color="success" size="small" variant="flat" :loading="bulkUpdatingOwners" @click="markSelectedOwnerPayoutsAsPaid">Mark Payouts as Paid</v-btn>
                                        <v-btn size="small" variant="text" @click="selectedOwnerIds = []">Clear</v-btn>
                                    </div>
                                    <v-spacer />
                                    <GenericActionDialog v-if="!isConfirmed" confirm-btn-text="Add Owners" title="Add Owners" :disabled="false" @confirm="confirm($event)">
                                        <template #activator="{ props: activatorProps }">
                                            <v-btn v-bind="activatorProps" color="primary" size="small" variant="text">+ Add Owner</v-btn>
                                        </template>
                                        <template #body>
                                            <AddOwnerTransactionsDialog :bundle-id="data.id" />
                                        </template>
                                    </GenericActionDialog>
                                </div>
                                <v-table v-if="owners.length" density="compact">
                                    <thead>
                                        <tr>
                                            <th v-if="isConfirmed" style="width: 40px">
                                                <v-checkbox-btn :model-value="allOwnersSelected" :indeterminate="someOwnersSelected && !allOwnersSelected" @update:model-value="toggleAllOwners" />
                                            </th>
                                            <th>Owner</th>
                                            <th class="text-center">Pending Payouts</th>
                                            <th class="text-center">Total Carbon (tCO2e)</th>
                                            <th class="text-center">Total Payout (MXN)</th>
                                            <th v-if="!isConfirmed" style="width: 50px" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="owner in owners" :key="owner.id">
                                            <td v-if="isConfirmed">
                                                <v-checkbox-btn :model-value="selectedOwnerIds.includes(owner.id)" :disabled="!isOwnerSelectable(owner)" @update:model-value="toggleOwner(owner.id)" />
                                            </td>
                                            <td>
                                                <Link :href="route('owners.show', { id: owner.id })">{{ owner.name }}</Link>
                                            </td>
                                            <td class="text-center">{{ owner.pending_payout_count ?? '-' }}</td>
                                            <td class="text-center">{{ formatNumber(owner.total_carbon, 2) }}</td>
                                            <td class="text-center">{{ formatNumber(owner.total_payout, 2) || '-' }}</td>
                                            <td v-if="!isConfirmed">
                                                <v-btn icon="mdi-close" size="x-small" variant="text" color="error" :loading="removingOwner && ownerToRemove?.id === owner.id" @click="removeOwner(owner)" />
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-table>
                                <p v-else class="text-body-2 text-medium-emphasis pa-4">No owners linked to this payout event.</p>
                            </v-tabs-window-item>
                        </v-tabs-window>
                    </v-card-text>
                </v-card>
            </v-container>
        </template>
    </AdminPanelTemplate>
</template>

<script setup lang="ts">
import { PayoutEvent, PayoutEventStatus } from '../../models/PayoutEvent';
import { useRoute } from '@shared/composables/useRoute';
import { useSubmitForm } from '@shared/composables/data-table/useSubmitForm';
import { Link } from '@inertiajs/vue3';
import type { PayoutEventOwnerSummary } from '@app/purchase/bundle/routes/routes';
import { Payout, PayoutStatus } from '@app/purchase/payout/models/Payout';
import { usePayoutEventRepository } from '@app/purchase/bundle/repositories/usePayoutEventRepository';
import { usePayoutRepository } from '@app/purchase/payout/repositories/usePayoutRepository';
import type { ValuesOfConst } from '@shared/types/ApiResponse';
import { useBlockRepository } from '@app/purchase/block/repositories/useBlockRepository.ts';
import { useOwnerRepository } from '@app/land-hold/owner/repositories/useOwnerRepository.ts';
import { useTransactionRepository } from '@app/purchase/transaction/repositories/useTransactionRepository.ts';
import { formatNumber } from '@shared/utils/formatNumber';

const page = PayoutEvent.routes().show.page;
const data = computed(() => page.props.data);
const payouts = computed(() => data.value.payouts?.data ?? []);
const owners = computed(() => data.value.owners?.data ?? []);

const { confirm, processing } = useSubmitForm();

const isConfirmed = computed(() => data.value.status === PayoutEventStatus.Confirmed);

const confirmDisabledReason = computed((): string | null => {
    if (isConfirmed.value) return 'This event has already been confirmed';
    if (!data.value.payout_price) return 'Set a payout price before confirming';
    if (!data.value.date) return 'Set an event date before confirming';
    if (!owners.value.length) return 'Add at least one owner before confirming';
    return null;
});
const canConfirm = computed(() => confirmDisabledReason.value === null);
const tab = ref('owners');
const confirming = ref(false);
const confirmError = ref('');
const selectedPayoutIds = ref<string[]>([]);
const selectedOwnerIds = ref<string[]>([]);
const bulkUpdating = ref(false);
const bulkUpdatingOwners = ref(false);

const unpaidPayouts = computed(() => payouts.value.filter((p) => p.status !== PayoutStatus.Paid));
const allPayoutsSelected = computed(() => unpaidPayouts.value.length > 0 && selectedPayoutIds.value.length === unpaidPayouts.value.length);
const somePayoutsSelected = computed(() => selectedPayoutIds.value.length > 0);

function isPayoutSelectable(payout: { status: ValuesOfConst<typeof PayoutStatus> }): boolean {
    return payout.status !== PayoutStatus.Paid;
}

function togglePayout(id: string): void {
    const index = selectedPayoutIds.value.indexOf(id);
    selectedPayoutIds.value = index === -1 ? [...selectedPayoutIds.value, id] : selectedPayoutIds.value.filter((pid) => pid !== id);
}

function toggleAllPayouts(checked: boolean): void {
    selectedPayoutIds.value = checked ? unpaidPayouts.value.map((p) => p.id) : [];
}

function isOwnerSelectable(owner: PayoutEventOwnerSummary): boolean {
    return payouts.value.some((p) => p.owner?.data.id === owner.id && p.status !== PayoutStatus.Paid);
}

function unpaidPayoutIdsForOwners(ownerIds: string[]): string[] {
    return payouts.value.filter((p) => ownerIds.includes(p.owner?.data.id ?? '') && p.status !== PayoutStatus.Paid).map((p) => p.id);
}

const selectableOwners = computed(() => owners.value.filter((o) => isOwnerSelectable(o)));
const allOwnersSelected = computed(() => selectableOwners.value.length > 0 && selectedOwnerIds.value.length === selectableOwners.value.length);
const someOwnersSelected = computed(() => selectedOwnerIds.value.length > 0);
const selectedOwnerPayoutCount = computed(() => unpaidPayoutIdsForOwners(selectedOwnerIds.value).length);

function toggleOwner(id: string): void {
    const index = selectedOwnerIds.value.indexOf(id);
    selectedOwnerIds.value = index === -1 ? [...selectedOwnerIds.value, id] : selectedOwnerIds.value.filter((oid) => oid !== id);
}

function toggleAllOwners(checked: boolean): void {
    selectedOwnerIds.value = checked ? selectableOwners.value.map((o) => o.id) : [];
}

function markSelectedOwnerPayoutsAsPaid(): void {
    const ids = unpaidPayoutIdsForOwners(selectedOwnerIds.value);
    if (!ids.length) return;
    bulkUpdatingOwners.value = true;
    usePayoutRepository()
        .command.bulkUpdate(ids, PayoutStatus.Paid)
        .execute({
            onSuccess: () => {
                selectedOwnerIds.value = [];
            },
            onFinish: () => {
                bulkUpdatingOwners.value = false;
            },
        });
}

function markSelectedAsPaid(): void {
    bulkUpdating.value = true;
    usePayoutRepository()
        .command.bulkUpdate(selectedPayoutIds.value, PayoutStatus.Paid)
        .execute({
            onSuccess: () => {
                selectedPayoutIds.value = [];
            },
            onFinish: () => {
                bulkUpdating.value = false;
            },
        });
}

const ownerToRemove = ref<PayoutEventOwnerSummary | null>(null);
const removingOwner = ref(false);

function removeOwner(owner: PayoutEventOwnerSummary): void {
    ownerToRemove.value = owner;
    removingOwner.value = true;
    usePayoutEventRepository()
        .command.detachOwner(data.value.id, owner.id)
        .execute({
            onFinish: () => {
                removingOwner.value = false;
            },
        });
}

function confirmEvent(close: () => void): void {
    confirming.value = true;
    confirmError.value = '';
    usePayoutEventRepository()
        .command.confirm(data.value.id)
        .execute({
            onSuccess: () => {
                close();
            },
            onError: (errors) => {
                confirmError.value = Object.values(errors).flat().join(', ') || 'An error occurred while confirming the event.';
            },
            onFinish: () => {
                confirming.value = false;
            },
        });
}
</script>
